import React, { useState } from 'react'
import {
  getSourceIcon,
  getBrowserIcon,
  getCountryFlag,
  getCountryName,
  getOSEmoji,
  getDeviceEmoji,
} from '@/lib/icons'
import { Tooltip, formatCount } from '@/components/ui/Tooltip'
// Revenue features disabled for MVP
// import { formatCurrency, formatPercentage } from '@/components/ui/Tooltip'
import type { TooltipRow } from '@/components/ui/Tooltip'
import { useTooltip } from '@/hooks/useTooltip'

export interface DistributionDataItem {
  label: string
  visitors: number
  revenue?: number
  conversions?: number
  conversionRate?: number
  revenuePerVisitor?: number
}

type IconType = 'source' | 'browser' | 'country' | 'os' | 'device' | 'path' | 'none'

interface HorizontalBarChartProps {
  data: DistributionDataItem[]
  iconType?: IconType
  maxItems?: number
  showDetails?: boolean
  onItemClick?: (item: DistributionDataItem) => void
}

function getIcon(label: string, iconType: IconType): React.ReactNode {
  switch (iconType) {
    case 'source':
      return getSourceIcon(label)
    case 'browser':
      return getBrowserIcon(label)
    case 'country':
      return <span>{getCountryFlag(label)}</span>
    case 'os':
      return <span>{getOSEmoji(label)}</span>
    case 'device':
      return <span>{getDeviceEmoji(label)}</span>
    case 'path':
      return <span>📄</span>
    default:
      return null
  }
}

function formatLabel(label: string, iconType: IconType): string {
  if (iconType === 'country') {
    return getCountryName(label)
  }
  if (!label) return 'Unknown'
  if (label === 'direct' || label.toLowerCase().includes('direct')) return 'Direct / None'
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function buildTooltipRows(item: DistributionDataItem): TooltipRow[] {
  const rows: TooltipRow[] = [
    { label: 'Visitors', value: formatCount(item.visitors) },
  ]

  // Revenue features disabled for MVP - uncomment when revenue tracking is enabled
  // const hasRevenueData = item.revenue !== undefined || item.conversionRate !== undefined
  //
  // if (hasRevenueData) {
  //   rows.push({
  //     label: 'Revenue',
  //     value: item.revenue !== undefined ? formatCurrency(item.revenue) : '-',
  //     color: 'green',
  //   })
  //   rows.push({
  //     label: 'Rev/Visitor',
  //     value: item.revenuePerVisitor !== undefined ? formatCurrency(item.revenuePerVisitor) : '-',
  //   })
  //   rows.push({
  //     label: 'Conv. Rate',
  //     value: item.conversionRate !== undefined ? formatPercentage(item.conversionRate) : '-',
  //   })
  // }

  return rows
}

export function HorizontalBarChart({
  data,
  iconType = 'none',
  maxItems = 5,
  showDetails = true,
  onItemClick,
}: HorizontalBarChartProps) {
  const [expanded, setExpanded] = useState(false)
  const { hoveredItem, tooltipPosition, handleMouseEnter, handleMouseMove, handleMouseLeave, isVisible } =
    useTooltip<DistributionDataItem>()

  if (!data || data.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500 text-sm">
        No data available
      </div>
    )
  }

  const maxValue = Math.max(...data.map((d) => d.visitors), 1)
  const displayData = expanded ? data : data.slice(0, maxItems)
  const hasMore = data.length > maxItems

  return (
    <div>
      <div className="space-y-3 p-4">
        {displayData.map((item, index) => {
          const percentage = (item.visitors / maxValue) * 100

          return (
            <div
              key={`${item.label}-${index}`}
              className={`group relative ${onItemClick ? 'cursor-pointer' : 'cursor-default'} hover:bg-gray-50 -mx-2 px-2 py-1 rounded-lg transition-colors`}
              onClick={() => onItemClick?.(item)}
              onMouseEnter={(e) => handleMouseEnter(item, e)}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <div className="flex items-center gap-3">
                {/* Icon */}
                {iconType !== 'none' && (
                  <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                    {getIcon(item.label, iconType)}
                  </div>
                )}

                {/* Label and bar */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {formatLabel(item.label, iconType)}
                    </span>
                    <div className="flex items-center gap-2 ml-2">
                      {/* Revenue display disabled for MVP - uncomment when revenue tracking is enabled */}
                      {/* {item.revenue !== undefined && item.revenue > 0 && (
                        <span className="text-xs font-medium text-green-600">
                          {formatCurrency(item.revenue)}
                        </span>
                      )} */}
                      <span className="text-sm font-semibold text-gray-700 tabular-nums">
                        {formatCount(item.visitors)}
                      </span>
                    </div>
                  </div>

                  {/* Bar */}
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: 'var(--color-chart-bar, #fed7aa)',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Tooltip */}
      {hoveredItem && (
        <Tooltip
          title={formatLabel(hoveredItem.label, iconType)}
          rows={buildTooltipRows(hoveredItem)}
          position={tooltipPosition}
          visible={isVisible}
        />
      )}

      {/* Details toggle */}
      {showDetails && hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1 border-t border-gray-100"
        >
          <span>{expanded ? 'Show less' : 'Show more'}</span>
          <svg
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  )
}
